import React, { useState, useMemo } from 'react';
import { Button } from '../../components/ui/Button';
import { SeedSku, DistributorProduct, ImportOptions } from '../../services/distributorService';
import { SEED_SKUS } from '../../pages/MarketplaceView';

type Domain = 'software' | 'computers' | 'networking' | 'mobile-hardware' | 'consulting' | 'telstra' | 'unified-collaboration';
type SortField = 'sku' | 'brand' | 'price' | 'soh' | 'domain' | 'status';
type SortDirection = 'asc' | 'desc';
const DEFAULT_DISTRIBUTOR_MARKUP_PERCENT = 18;

const formatDomainLabel = (domain: string) => {
  switch (domain) {
    case 'mobile-hardware':
      return 'Telstra Handsets';
    case 'unified-collaboration':
      return 'Unified Collaboration';
    default:
      return domain.replace('-', ' ');
  }
};

interface ProductsTableProps {
  seedSkus: SeedSku[];
  products: DistributorProduct[];
  onSaveSkus: (skus: SeedSku[]) => Promise<boolean>;
  onDeleteSkusAndProducts: (skus: string[]) => Promise<boolean>;
  onImportProducts: (products: DistributorProduct[], options: ImportOptions) => Promise<boolean>;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
}

interface CombinedProduct {
  sku: string;
  enabled: boolean;
  domain: string;
  product: DistributorProduct | null;
  status: 'not_synced' | 'synced' | 'disabled';
}

export function ProductsTable({
  seedSkus,
  products,
  onSaveSkus,
  onDeleteSkusAndProducts,
  onImportProducts,
  onError,
  onSuccess
}: ProductsTableProps) {
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set());
  const [searchFilter, setSearchFilter] = useState('');
  const [sortField, setSortField] = useState<SortField>('sku');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Add SKU modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [bulkAddText, setBulkAddText] = useState('');
  const [bulkAddDomain, setBulkAddDomain] = useState<Domain>('computers');
  const [newSku, setNewSku] = useState('');
  const [newSkuDomain, setNewSkuDomain] = useState<Domain>('computers');
  
  // Import modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [markupPercent, setMarkupPercent] = useState<number>(DEFAULT_DISTRIBUTOR_MARKUP_PERCENT);
  const [importing, setImporting] = useState(false);
  
  const [loading, setLoading] = useState(false);

  // Create product lookup map
  const productMap = useMemo(() => {
    const map = new Map<string, DistributorProduct>();
    products.forEach(p => map.set(p.sku, p));
    return map;
  }, [products]);

  // Combine SKUs and products into unified list
  const combinedProducts = useMemo((): CombinedProduct[] => {
    return seedSkus.map(s => ({
      sku: s.sku,
      enabled: s.enabled,
      domain: s.category || 'computers',
      product: productMap.get(s.sku) || null,
      status: !s.enabled ? 'disabled' as const : productMap.has(s.sku) ? 'synced' as const : 'not_synced' as const
    }));
  }, [seedSkus, productMap]);

  // Filter and sort
  const filteredProducts = useMemo(() => {
    let result = [...combinedProducts];

    if (searchFilter.trim()) {
      const query = searchFilter.toLowerCase();
      result = result.filter(item =>
        item.sku.toLowerCase().includes(query) ||
        item.domain.toLowerCase().includes(query) ||
        (item.product?.brand || '').toLowerCase().includes(query) ||
        (item.product?.description || '').toLowerCase().includes(query)
      );
    }

    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'sku':
          comparison = a.sku.localeCompare(b.sku);
          break;
        case 'brand':
          comparison = (a.product?.brand || '').localeCompare(b.product?.brand || '');
          break;
        case 'price':
          comparison = (a.product?.unitPrice || 0) - (b.product?.unitPrice || 0);
          break;
        case 'soh':
          comparison = (a.product?.soh || 0) - (b.product?.soh || 0);
          break;
        case 'domain':
          comparison = a.domain.localeCompare(b.domain);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [combinedProducts, searchFilter, sortField, sortDirection]);

  const syncedProducts = combinedProducts.filter(p => p.status === 'synced');
  const enabledCount = seedSkus.filter(s => s.enabled).length;
  const allSelected = filteredProducts.length > 0 && filteredProducts.every(p => selectedSkus.has(p.sku));
  const someSelected = selectedSkus.size > 0;
  const selectedSynced = Array.from(selectedSkus).filter(sku => productMap.has(sku));

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedSkus(new Set());
    } else {
      setSelectedSkus(new Set(filteredProducts.map(p => p.sku)));
    }
  };

  const toggleSelect = (sku: string) => {
    setSelectedSkus(prev => {
      const next = new Set(prev);
      if (next.has(sku)) {
        next.delete(sku);
      } else {
        next.add(sku);
      }
      return next;
    });
  };

  // Add single SKU
  const handleAddSku = async () => {
    const sku = newSku.trim().toUpperCase();
    if (!sku) return;

    if (seedSkus.some(s => s.sku === sku)) {
      onError(`SKU ${sku} is already tracked`);
      return;
    }

    setLoading(true);
    const updatedSkus: SeedSku[] = [...seedSkus, { sku, enabled: true, category: newSkuDomain, name: null }];
    const success = await onSaveSkus(updatedSkus);
    setLoading(false);

    if (success) {
      setNewSku('');
      onSuccess(`Added SKU ${sku}`);
    }
  };

  // Bulk add SKUs
  const handleBulkAdd = async () => {
    const lines = bulkAddText.split(/[\n,;]+/).map(s => s.trim().toUpperCase()).filter(Boolean);
    const existingSkus = new Set(seedSkus.map(s => s.sku));
    const newSkus = lines.filter(sku => !existingSkus.has(sku));

    if (newSkus.length === 0) {
      onError('All SKUs are already tracked');
      return;
    }

    setLoading(true);
    const updatedSkus: SeedSku[] = [
      ...seedSkus,
      ...newSkus.map(sku => ({ sku, enabled: true, category: bulkAddDomain, name: null }))
    ];
    const success = await onSaveSkus(updatedSkus);
    setLoading(false);

    if (success) {
      setBulkAddText('');
      setShowAddModal(false);
      onSuccess(`Added ${newSkus.length} SKUs`);
    }
  };

  // Initialize defaults
  const handleInitializeDefaults = async () => {
    if (seedSkus.length > 0) {
      const confirm = window.confirm('This will add default SKUs to your existing list. Continue?');
      if (!confirm) return;
    }

    setLoading(true);
    const existingSkus = new Set(seedSkus.map(s => s.sku));
    const newDefaults = SEED_SKUS.filter(s => !existingSkus.has(s.sku));
    const updatedSkus: SeedSku[] = [
      ...seedSkus,
      ...newDefaults.map(s => ({ 
        sku: s.sku, 
        category: s.category?.toLowerCase() || 'computers', 
        name: s.name || null, 
        enabled: true 
      }))
    ];
    const success = await onSaveSkus(updatedSkus);
    setLoading(false);

    if (success) {
      onSuccess(`Added ${newDefaults.length} default SKUs`);
    }
  };

  // Toggle enable/disable
  const handleToggle = async (sku: string) => {
    setLoading(true);
    const updatedSkus = seedSkus.map(s => 
      s.sku === sku ? { ...s, enabled: !s.enabled } : s
    );
    await onSaveSkus(updatedSkus);
    setLoading(false);
  };

  // Bulk enable
  const handleEnableSelected = async () => {
    setLoading(true);
    const updatedSkus = seedSkus.map(s => 
      selectedSkus.has(s.sku) ? { ...s, enabled: true } : s
    );
    const success = await onSaveSkus(updatedSkus);
    setLoading(false);
    if (success) {
      onSuccess(`Enabled ${selectedSkus.size} SKUs`);
    }
  };

  // Bulk disable
  const handleDisableSelected = async () => {
    setLoading(true);
    const updatedSkus = seedSkus.map(s => 
      selectedSkus.has(s.sku) ? { ...s, enabled: false } : s
    );
    const success = await onSaveSkus(updatedSkus);
    setLoading(false);
    if (success) {
      onSuccess(`Disabled ${selectedSkus.size} SKUs`);
    }
  };

  // Remove selected (deletes SKUs AND products)
  const handleRemoveSelected = async () => {
    const confirmed = window.confirm(
      `Remove ${selectedSkus.size} SKU${selectedSkus.size > 1 ? 's' : ''}? This will also remove any synced product data.`
    );
    if (!confirmed) return;

    setLoading(true);
    const success = await onDeleteSkusAndProducts(Array.from(selectedSkus));
    setLoading(false);

    if (success) {
      setSelectedSkus(new Set());
      onSuccess(`Removed ${selectedSkus.size} SKUs`);
    }
  };

  // Import selected to catalogue
  const handleOpenImportModal = () => {
    if (selectedSynced.length === 0) {
      onError('No synced products selected. Only synced products can be imported.');
      return;
    }
    setShowImportModal(true);
  };

  const handleImport = async () => {
    setImporting(true);

    // Group by domain
    const productsByDomain: Record<string, DistributorProduct[]> = {};
    selectedSynced.forEach(sku => {
      const item = combinedProducts.find(p => p.sku === sku);
      if (item?.product) {
        const domain = item.domain;
        if (!productsByDomain[domain]) {
          productsByDomain[domain] = [];
        }
        productsByDomain[domain].push(item.product);
      }
    });

    let allSuccess = true;
    for (const domain of Object.keys(productsByDomain)) {
      const domainProducts = productsByDomain[domain];
      const options: ImportOptions = {
        domain: domain as Domain,
        markupPercent: markupPercent
      };
      const success = await onImportProducts(domainProducts, options);
      if (!success) allSuccess = false;
    }

    setImporting(false);
    if (allSuccess) {
      setSelectedSkus(new Set());
      setShowImportModal(false);
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <span className="text-neutral-300">↕</span>;
    }
    return <span className="text-primary-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const getStatusBadge = (status: CombinedProduct['status']) => {
    switch (status) {
      case 'synced':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">Synced</span>;
      case 'not_synced':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Pending Sync</span>;
      case 'disabled':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-500">Disabled</span>;
    }
  };

  const getSohBadge = (soh: number | null | undefined) => {
    const value = soh ?? 0;
    if (value > 10) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">{value}</span>;
    } else if (value > 0) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">{value}</span>;
    }
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-600">0</span>;
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-neutral-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              Tracked Products ({seedSkus.length})
            </h2>
            <p className="text-sm text-neutral-500 mt-1">
              {enabledCount} enabled • {syncedProducts.length} synced
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowAddModal(true)}>
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add SKUs
            </Button>
            {seedSkus.length === 0 && (
              <Button variant="outline" onClick={handleInitializeDefaults} disabled={loading}>
                Initialize Defaults
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Quick Add Row */}
      <div className="px-6 py-4 bg-neutral-50 border-b border-neutral-200">
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={newSku}
            onChange={(e) => setNewSku(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleAddSku()}
            placeholder="Enter SKU (e.g. B9GC5PT)"
            className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm font-mono"
          />
          <select
            value={newSkuDomain}
            onChange={(e) => setNewSkuDomain(e.target.value as Domain)}
            className="px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white"
            aria-label="Domain for new SKU"
          >
            <option value="computers">Computers</option>
            <option value="networking">Networking</option>
            <option value="software">Software</option>
            <option value="consulting">Consulting</option>
            <option value="unified-collaboration">Unified Collaboration</option>
          </select>
          <Button onClick={handleAddSku} disabled={!newSku.trim() || loading}>
            Add
          </Button>
        </div>
      </div>

      {/* Search and Bulk Actions */}
      <div className="px-6 py-4 border-b border-neutral-200">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Search SKUs, brands, descriptions..."
              className="w-full pl-9 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
            <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {someSelected && (
            <div className="flex items-center gap-2 border-l border-neutral-200 pl-4">
              <span className="text-sm text-neutral-600 font-medium">{selectedSkus.size} selected</span>
              <Button size="sm" variant="outline" onClick={handleEnableSelected} disabled={loading}>
                Enable
              </Button>
              <Button size="sm" variant="outline" onClick={handleDisableSelected} disabled={loading}>
                Disable
              </Button>
              {selectedSynced.length > 0 && (
                <Button size="sm" onClick={handleOpenImportModal}>
                  Import ({selectedSynced.length})
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={handleRemoveSelected}
                disabled={loading}
                className="text-rose-600 hover:text-rose-700 border-rose-200 hover:border-rose-300"
              >
                Remove
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      {filteredProducts.length > 0 ? (
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left w-12">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    aria-label="Select all"
                  />
                </th>
                <th className="px-4 py-3 text-left w-12">
                  <span className="text-xs font-medium text-neutral-500 uppercase">On</span>
                </th>
                <th className="px-4 py-3 text-left">
                  <button onClick={() => handleSort('sku')} className="flex items-center gap-1 text-xs font-medium text-neutral-500 uppercase tracking-wider hover:text-neutral-700">
                    SKU <SortIcon field="sku" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button onClick={() => handleSort('brand')} className="flex items-center gap-1 text-xs font-medium text-neutral-500 uppercase tracking-wider hover:text-neutral-700">
                    Brand <SortIcon field="brand" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left max-w-xs">
                  <span className="text-xs font-medium text-neutral-500 uppercase">Description</span>
                </th>
                <th className="px-4 py-3 text-left">
                  <button onClick={() => handleSort('domain')} className="flex items-center gap-1 text-xs font-medium text-neutral-500 uppercase tracking-wider hover:text-neutral-700">
                    Domain <SortIcon field="domain" />
                  </button>
                </th>
                <th className="px-4 py-3 text-right">
                  <button onClick={() => handleSort('price')} className="flex items-center gap-1 text-xs font-medium text-neutral-500 uppercase tracking-wider hover:text-neutral-700 ml-auto">
                    Buy Price <SortIcon field="price" />
                  </button>
                </th>
                <th className="px-4 py-3 text-right">
                  <button onClick={() => handleSort('soh')} className="flex items-center gap-1 text-xs font-medium text-neutral-500 uppercase tracking-wider hover:text-neutral-700 ml-auto">
                    SOH <SortIcon field="soh" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button onClick={() => handleSort('status')} className="flex items-center gap-1 text-xs font-medium text-neutral-500 uppercase tracking-wider hover:text-neutral-700">
                    Status <SortIcon field="status" />
                  </button>
                </th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {filteredProducts.map((item) => (
                <tr key={item.sku} className={`hover:bg-neutral-50 ${!item.enabled ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedSkus.has(item.sku)}
                      onChange={() => toggleSelect(item.sku)}
                      className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                      aria-label={`Select ${item.sku}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={item.enabled}
                      onChange={() => handleToggle(item.sku)}
                      className="h-4 w-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500"
                      title="Enable/disable sync"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm font-medium text-neutral-900">{item.sku}</span>
                  </td>
                  <td className="px-4 py-3">
                    {item.product?.brand ? (
                      <span className="text-sm text-neutral-900">{item.product.brand}</span>
                    ) : (
                      <span className="text-sm text-neutral-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    {item.product?.description ? (
                      <span className="text-sm text-neutral-600 truncate block">{item.product.description}</span>
                    ) : (
                      <span className="text-sm text-neutral-400 italic">Sync to fetch details</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 capitalize">
                      {formatDomainLabel(item.domain)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {item.product?.unitPrice != null ? (
                      <span className="text-sm text-neutral-900">${item.product.unitPrice.toFixed(2)}</span>
                    ) : (
                      <span className="text-sm text-neutral-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {item.product ? getSohBadge(item.product.soh) : <span className="text-sm text-neutral-400">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(item.status)}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={async () => {
                        if (window.confirm(`Remove SKU ${item.sku}?`)) {
                          await onDeleteSkusAndProducts([item.sku]);
                        }
                      }}
                      className="text-neutral-400 hover:text-rose-600 transition-colors"
                      title="Remove SKU"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : seedSkus.length > 0 ? (
        <div className="text-center py-12 text-neutral-500">
          <p className="text-sm">No products match your search.</p>
        </div>
      ) : (
        <div className="text-center py-12 text-neutral-500">
          <svg className="w-12 h-12 mx-auto text-neutral-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <p className="text-sm">No SKUs configured yet.</p>
          <p className="text-xs text-neutral-400 mt-1">
            Add SKUs to track or click "Initialize Defaults" to start with a curated list.
          </p>
        </div>
      )}

      {/* Footer Stats */}
      {seedSkus.length > 0 && (
        <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200 flex items-center justify-between text-sm text-neutral-500">
          <span>
            {filteredProducts.length} of {seedSkus.length} products
            {searchFilter && ` matching "${searchFilter}"`}
          </span>
          <span>
            {syncedProducts.length} synced • {seedSkus.length - syncedProducts.length} pending
          </span>
        </div>
      )}

      {/* Add SKUs Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowAddModal(false)} />
            
            <div className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-6 py-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Bulk Add SKUs</h3>
                  <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600" title="Close" aria-label="Close">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Domain</label>
                    <select
                      value={bulkAddDomain}
                      onChange={(e) => setBulkAddDomain(e.target.value as Domain)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                      aria-label="Domain for bulk add"
                    >
                      <option value="computers">Computers</option>
                      <option value="networking">Networking</option>
                      <option value="software">Software</option>
                      <option value="consulting">Consulting</option>
                      <option value="unified-collaboration">Unified Collaboration</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SKUs (one per line or comma-separated)</label>
                    <textarea
                      value={bulkAddText}
                      onChange={(e) => setBulkAddText(e.target.value.toUpperCase())}
                      placeholder="B9GC5PT&#10;21SG000LAU&#10;5WS0V08541"
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm font-mono"
                    />
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
                <Button onClick={handleBulkAdd} disabled={!bulkAddText.trim() || loading}>
                  Add SKUs
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowImportModal(false)} />
            
            <div className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-6 py-5">
                <h3 className="text-lg font-semibold text-gray-900">
                  Import {selectedSynced.length} Products to Catalogue
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Configure markup before importing to your catalogue.
                </p>
                
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Markup Percentage</label>
                    <p className="text-sm text-gray-500 mt-1">
                      Sell price = Buy price + markup. Automatically adjusts when prices change.
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <input
                      type="number"
                      min="0"
                      max="200"
                      value={markupPercent}
                      onChange={(e) => setMarkupPercent(Number(e.target.value))}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      aria-label="Markup percentage"
                    />
                    <span className="text-sm text-gray-600">% markup over buy price</span>
                  </div>

                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      Products will be imported to their assigned domains. You can customize names and descriptions after import.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button>
                <Button onClick={handleImport} disabled={importing}>
                  {importing ? 'Importing...' : 'Import Products'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductsTable;
